import { CancellableRequest } from '@services/HttpService'
import { EnhancedListDataStore } from './EnhancedListDataStore'
import { nav, stores } from '@stores'
import {
  IA_COMMON_DELIVERY_ID,
  MANDATORY_ATTRIBUTE_FOR_INITIAL_ALLOCATION
} from '@modules/order/constants/uiConstants'
import InitialAllocationListDataService from '@modules/order/services/InitialAllocationListDataService'
import {
  getCurrentGroupMapping,
  mapNewFiltersToIds
} from '@modules/order/utils/dynamic-group-mapping'
import { ObservableMap, action, observable } from 'mobx'

export class InitialAllocationDataStore extends EnhancedListDataStore {
  private initialAllocationservice = new InitialAllocationListDataService()
  @observable IAProducts = new ObservableMap()
  @observable IAProductsLoding = new ObservableMap()
  @observable isSizeQtyUpdated = false
  @observable productPercentToggleStates: {
    [productId: string]: { isPercentMode: boolean; percentValue: Number }
  } = {}
  @observable IAProductQuantityCache = null

  fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number
  ): CancellableRequest<any> | Promise<any[]> {
    const {
      nav: {
        queryParams: { orderId }
      },
      productConfiguration: { initialAllocationActivated }
    } = stores

    if (!orderId) return undefined
    if (!initialAllocationActivated) return undefined
    return super.fetchItemsFromAPI(fromIndex, toIndex, this.fetchInitialAllocationItems)
  }

  fetchHierarchyFromAPI(): CancellableRequest<any> | Promise<any> {
    const {
      nav: {
        queryParams: { orderId }
      },
      productConfiguration: { initialAllocationActivated }
    } = stores

    if (!orderId) return undefined
    if (!initialAllocationActivated) return undefined

    return super.fetchHierarchyFromAPI()
  }

  fetchInitialAllocationItems = (fromIndex, toIndex) => {
    const {
      nav: {
        queryParams: { buyingSessionGroupId, orderId }
      },
      productConfiguration: { findSpecificAttribute, defaultValuesFromConfig }
    } = stores

    let mapping = getCurrentGroupMapping(
      this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0),
      defaultValuesFromConfig?.empty,
      findSpecificAttribute
    )
    // TODO: Need to move deliveryId logic to somewhere else.

    const body = {
      includeAllDoorOrders: true,
      ...this.getAllTopBarSelections(true),
      filters: {
        ...(mapping ? mapping : {}),
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...this.getAppliedWarningFilterData()
      },
      attributes: MANDATORY_ATTRIBUTE_FOR_INITIAL_ALLOCATION
    }
    this.initialAllocationservice
      .fetchIAProducts(buyingSessionGroupId, orderId, IA_COMMON_DELIVERY_ID, body)
      .then(resp => {
        this.updateIAProducts(resp.data ?? [])
      })
  }

  /* Use this method to get updated product data from backend */
  @action
  fetchSingleProductDataFromAPI = (productId, operationAttributes = []) => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores
    const productPayload = {
      includeAllDoorOrders: true,
      filters: {
        clientOrderProductId: [productId]
      },
      attributes: operationAttributes?.length
        ? [...operationAttributes]
        : MANDATORY_ATTRIBUTE_FOR_INITIAL_ALLOCATION
    }
    return this.initialAllocationservice
      .fetchIAProducts(
        buyingSessionGroupId,
        orderId,
        IA_COMMON_DELIVERY_ID,
        productPayload
      )
      .then(resp => {
        return resp?.data?.[0]
      })
      .catch(e => {
        throw new Error(e)
      })
  }

  /* Use this method to get single product data from local object */
  getIAProductData = productId => {
    if (this.IAProducts.has(productId)) {
      return this.IAProducts.get(productId)
    }
    return null
  }

  getIAProductDataLoading = productId => {
    if (this.IAProductsLoding.has(productId)) {
      return this.IAProductsLoding.get(productId)
    }
    return false
  }

  /* Use this method to update multiple products into local object */
  @action
  updateIAProducts(products: Object[]) {
    products.forEach(product => {
      this.updateSingleProduct(product)
    })
  }

  /* Use this method to update single product from local object */
  @action
  updateSingleProduct(product) {
    if (product) {
      if (this.IAProducts.has(product.id)) {
        const currentProduct = this.IAProducts.get(product.id)
        this.IAProducts.set(product.id, { ...currentProduct, ...product })
      } else {
        this.IAProducts.set(product.id, product)
      }
    }
  }

  @action
  updateIAProductQuantity(productId, payload) {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores

    return this.initialAllocationservice
      .updateIAProductQuantity(
        buyingSessionGroupId,
        productId,
        orderId,
        IA_COMMON_DELIVERY_ID,
        payload
      )
      .then(response => {
        const { heirarchy } = response
        this.fetchSingleProductDataFromAPI(productId, [
          'deliveryProductQuantity',
          'deliveryRemainingProductQuantity'
        ]).then(({ deliveryProductQuantity, deliveryRemainingProductQuantity } = {}) => {
          const newProduct = {
            ...this.getIAProductData(productId),
            deliveryProductQuantity,
            deliveryRemainingProductQuantity
          }
          this.mutateProductHierarchy(heirarchy)
          this.updateSingleProduct(newProduct)
        })
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        this.clearIAProductQuantityCache()
      })
  }

  @action
  updateProductPercentToggleState = (
    flag: boolean,
    percentVal: Number,
    productId: string
  ) => {
    this.productPercentToggleStates = {
      ...this.productPercentToggleStates,
      [productId]: { isPercentMode: flag, percentValue: percentVal }
    }
  }

  @action
  updateDoorLevelTotalQuantity(productId: string, payload: any, updatedIAProduct: any) {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores

    return this.initialAllocationservice
      .updateDoorLevelTotalQuantity(
        buyingSessionGroupId,
        productId,
        orderId,
        payload?.doorOrderId,
        IA_COMMON_DELIVERY_ID,
        payload
      )
      .then(resp => {
        const { heirarchy, totalDoorHierarchyKPIs, singleProductAggregation } = resp
        this.mutateProductHierarchy(heirarchy)
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
        const newProductData = singleProductAggregation?.children?.[0]
        if (newProductData) {
          const totalDeliveryDoorQuantity = newProductData.totalDeliveryDoorQuantity || 0
          const totalDeliverySizeQuantity = newProductData.totalDeliverySizeQuantity || 0
          updatedIAProduct.deliverySizeQuantity = totalDeliverySizeQuantity
          updatedIAProduct.deliveryDoorQuantity = totalDeliveryDoorQuantity
        }
        this.updateSingleProduct(updatedIAProduct)
      })
      .catch(e => {
        console.error(e)
      })
  }

  @action
  updateIASizeQuantity = (
    productId: string,
    payload: any,
    doorOrderId: string,
    sizeId: string,
    updatedIAProduct: any
  ) => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores
    const clientOrderId = orderId
    this.isSizeQtyUpdated = true
    return this.initialAllocationservice
      .updateIASizeQuantity(
        buyingSessionGroupId,
        productId,
        clientOrderId,
        doorOrderId,
        payload,
        sizeId,
        IA_COMMON_DELIVERY_ID
      )
      .then(resp => {
        const { singleProductAggregation } = resp
        const newProductData = singleProductAggregation?.children?.[0]
        if (newProductData) {
          const totalDeliverySizeQuantity = newProductData.totalDeliverySizeQuantity || 0
          updatedIAProduct.deliverySizeQuantity = totalDeliverySizeQuantity
        }
        this.updateSingleProduct(updatedIAProduct)
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        this.isSizeQtyUpdated = false
      })
  }

  @action
  clearIAProducts() {
    this.IAProducts.replace(new Map())
  }

  @action
  clearIAProductsLoading() {
    this.IAProductsLoding.replace(new Map())
  }

  @action
  clearProductPercentToggleState() {
    this.productPercentToggleStates = {}
  }

  @action setIAProductQuantityCache(value) {
    this.IAProductQuantityCache = value
  }

  @action clearIAProductQuantityCache() {
    this.IAProductQuantityCache = null
  }

  @action
  destroy() {
    super.destroy()
    this.clearIAProducts()
    this.clearIAProductsLoading()
    this.clearProductPercentToggleState()
    this.clearIAProductQuantityCache()
  }

  @action
  updateDeliveryAllocatedProductQuantity = (productId, payload) => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores

    return this.initialAllocationservice
      .updateDeliveryAllocatedProductQuantity(
        buyingSessionGroupId,
        productId,
        orderId,
        IA_COMMON_DELIVERY_ID,
        payload
      )
      .then(() => {
        this.fetchSingleProductDataFromAPI(productId, [
          'deliveryAllocatedProductQuantity',
          'deliveryRemainingProductQuantity'
        ]).then(
          ({
            deliveryAllocatedProductQuantity,
            deliveryRemainingProductQuantity
          } = {}) => {
            const newProduct = {
              ...this.getIAProductData(productId),
              deliveryAllocatedProductQuantity,
              deliveryRemainingProductQuantity
            }

            this.updateSingleProduct(newProduct)
          }
        )
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => this.IAProductsLoding.set(productId, false))
  }

  @action
  bulkUpdateDeliveryDoorOrderProducts = (summary, productId, payload) => {
    const clientOrderId = summary?.clientOrder?.clientOrderId || summary?.clientOrder?.id
    const {
      queryParams: { buyingSessionGroupId }
    } = nav

    this.IAProductsLoding.set(productId, true)

    return this.initialAllocationservice
      .bulkUpdateDeliveryDoorOrderProducts(
        buyingSessionGroupId,
        clientOrderId,
        productId,
        IA_COMMON_DELIVERY_ID,
        payload
      )
      .then(resp => {
        const { heirarchy, totalDoorHierarchyKPIs } = resp

        this.mutateProductHierarchy(heirarchy)
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)

        this.fetchSingleProductDataFromAPI(
          productId,
          MANDATORY_ATTRIBUTE_FOR_INITIAL_ALLOCATION
        ).then(productResp => {
          const updatedProduct = {
            ...this.getIAProductData(productId),
            ...productResp
          }
          this.updateSingleProduct(updatedProduct)
        })
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        this.IAProductsLoding.set(productId, false)
        this.clearIAProductQuantityCache()
      })
  }

  @action
  rollUpIAProductQuantity = (productId, payload) => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId }
      }
    } = stores

    this.IAProductsLoding.set(productId, true)
    return this.initialAllocationservice
      .rollUpIAProductQuantity(
        buyingSessionGroupId,
        productId,
        orderId,
        IA_COMMON_DELIVERY_ID,
        payload
      )
      .then(response => {
        const { heirarchy, totalDoorHierarchyKPIs } = response
        this.mutateProductHierarchy(heirarchy)
        this.updateTotalDoorHierarchy(totalDoorHierarchyKPIs)
        this.fetchSingleProductDataFromAPI(productId).then(productData => {
          if (productData) {
            const newProduct = {
              ...this.getIAProductData(productId),
              ...productData
            }
            this.updateSingleProduct(newProduct)
          }
        })
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        this.IAProductsLoding.set(productId, false)
      })
  }
}
